/**
 * Referral partner links: thecontainertrader.com/any-page?ref=CODE
 *
 * On page load we send the code to the server, which checks it and sets the 90-day ct_ref
 * cookie (last link used wins). Then the ref parameter is removed from the address bar so it is
 * not shared again by accident. Checkout and signup read the cookie on the server.
 */
import { referralCheck, referralSignup, referralVisit } from './api';

const CODE_RE = /^[a-z0-9][a-z0-9-]{1,22}[a-z0-9]$/;
const STORAGE_KEY = 'ctReferralCode';
const WINDOW_MS = 90 * 864e5;

/** Lowercased code when it has a valid format, else null. */
export const normalizeReferralCode = raw => {
  const code = `${raw == null ? '' : raw}`.trim().toLowerCase();
  return CODE_RE.test(code) && !code.includes('--') ? code : null;
};

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

/** Remembers the code from a partner link so the signup form can fill it in. */
export const rememberReferralCode = (code, now = Date.now()) => {
  const s = storage();
  if (!s || !code) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify({ code, at: now }));
  } catch (e) {
    // ignore
  }
};

/** The code from the last partner link used in the past 90 days, or null. */
export const getRememberedReferralCode = (now = Date.now()) => {
  const s = storage();
  if (!s) return null;
  try {
    const v = JSON.parse(s.getItem(STORAGE_KEY) || 'null');
    const code = normalizeReferralCode(v && v.code);
    return code && now - Number(v.at) < WINDOW_MS ? code : null;
  } catch (e) {
    return null;
  }
};

const checkCache = {};
/** Resolves true when the code belongs to an active partner. Results are cached per code. */
export const isActiveReferralCode = code => {
  const c = normalizeReferralCode(code);
  if (!c) return Promise.resolve(false);
  if (!checkCache[c]) {
    checkCache[c] = referralCheck(c)
      .then(r => !!(r && r.ok))
      .catch(() => {
        delete checkCache[c];
        return true; // never block signup because the check failed
      });
  }
  return checkCache[c];
};

// Code typed on the signup form, sent with the signup credit call.
let pendingCode = null;
export const setPendingReferralCode = code => {
  pendingCode = normalizeReferralCode(code);
};

export const readRefParam = search => {
  if (!search) return null;
  try {
    const code = (new URLSearchParams(search).get('ref') || '').trim().toLowerCase();
    return CODE_RE.test(code) ? code : null;
  } catch (e) {
    return null;
  }
};

/** Search string without the ref parameter ('' when nothing is left). */
export const searchWithoutRef = search => {
  const params = new URLSearchParams(search || '');
  params.delete('ref');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

/** Call once on the client after the app mounts. Never throws. */
export const captureReferralFromUrl = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const { pathname, search, hash } = window.location;
  if (!/[?&]ref=/i.test(search || '')) return Promise.resolve(null);
  const code = readRefParam(search);
  try {
    window.history.replaceState(
      window.history.state,
      '',
      `${pathname}${searchWithoutRef(search)}${hash || ''}`
    );
  } catch (e) {
    // ignore
  }
  if (!code) return Promise.resolve(null);
  return referralVisit({
    code,
    path: pathname,
    referrer: (typeof document !== 'undefined' && document.referrer) || '',
  })
    .then(r => {
      if (r && r.ok && r.code) rememberReferralCode(r.code);
      return r;
    })
    .catch(() => null);
};

/** After a new account is created: credit the partner whose link was used. Never throws. */
export const creditReferralSignup = () => {
  if (typeof window === 'undefined') return;
  try {
    const code = pendingCode;
    pendingCode = null;
    referralSignup(code ? { code } : {}).catch(() => null);
  } catch (e) {
    // ignore
  }
};
